import { RiskRecognitionsRequester } from '@cobliteam/api-lib/src/dashboard/riskRecognitions/RiskRecognitionsRequester';
import type { RiskRecognitionHighlight } from '@cobliteam/api-lib/src/dashboard/riskRecognitions/Types';
import { Environment } from '@cobliteam/react-environments';
import { type InfiniteData, type QueryKey, useMutation, useQueryClient } from '@tanstack/react-query';
import { RISK_RECOGNITION_HIGHLIGHT_QUERY_KEY, RISK_RECOGNITION_LIST_QUERY_KEY } from './constants';

const env = Environment.getInstance().getEnvironment();
const getRiskRecognitionsRequester = () => new RiskRecognitionsRequester({ host: env.apiHost });

type ListCache = InfiniteData<RiskRecognitionHighlight[], string | null>;
type Snapshots = [QueryKey, ListCache | undefined][];

// Remove o item (por id) de todas as páginas de todos os caches de listagem (ativas/arquivadas/ambas).
const optimisticRemove = (queryClient: ReturnType<typeof useQueryClient>, detectionId: string) =>
  queryClient.setQueriesData<ListCache>({ queryKey: [RISK_RECOGNITION_LIST_QUERY_KEY] }, (old) =>
    old ? { ...old, pages: old.pages.map((page) => page.filter((item) => item.id !== detectionId)) } : old
  );

/**
 * Toggle de arquivamento com update otimista: o item some das listas na hora; em erro, rollback pelo
 * snapshot; em qualquer desfecho, invalida as listas + o highlight corrente pra reconciliar com o servidor.
 * Idempotente no backend, então retry/duplo-clique é seguro.
 */
const useToggleArchive = (action: (detectionId: string) => Promise<unknown>) => {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, string, { snapshots: Snapshots }>({
    mutationFn: (detectionId) => action(detectionId),
    onMutate: async (detectionId) => {
      await queryClient.cancelQueries({ queryKey: [RISK_RECOGNITION_LIST_QUERY_KEY] });
      const snapshots = queryClient.getQueriesData<ListCache>({ queryKey: [RISK_RECOGNITION_LIST_QUERY_KEY] });
      optimisticRemove(queryClient, detectionId);
      return { snapshots };
    },
    onError: (_error, _detectionId, context) => {
      for (const [key, data] of context?.snapshots ?? []) queryClient.setQueryData(key, data);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [RISK_RECOGNITION_LIST_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [RISK_RECOGNITION_HIGHLIGHT_QUERY_KEY] });
    },
  });
};

const useArchiveRiskRecognition = () => useToggleArchive((id) => getRiskRecognitionsRequester().archiveHighlight(id));
const useUnarchiveRiskRecognition = () => useToggleArchive((id) => getRiskRecognitionsRequester().unarchiveHighlight(id));

export { useArchiveRiskRecognition, useUnarchiveRiskRecognition };
